import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, X, Building2, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

const API = API_BASE_URL;

export default function AddContactModal({ open, onOpenChange, contact = null, prefillPhone = '', onSaved }) {
  const isEdit = !!contact;
  
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    company: '',
    email: '',
    notes: '',
    source: 'company',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (contact) {
        setFormData({
          name: contact.name || '',
          phone_number: contact.phone_number || '',
          company: contact.company || '',
          email: contact.email || '',
          notes: contact.notes || '',
          source: contact.source || 'company',
          tags: contact.tags || []
        });
      } else {
        setFormData({
          name: '',
          phone_number: prefillPhone || '',
          company: '',
          email: '',
          notes: '',
          source: 'company',
          tags: []
        });
      }
      setTagInput('');
    }
  }, [open, contact, prefillPhone]);

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.phone_number.trim()) {
      toast.error('Name and phone number are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (isEdit) {
        await axios.put(`${API}/api/contacts/${contact.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact updated');
      } else {
        // Create
        await axios.post(`${API}/api/contacts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact added');
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'add'} contact`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Edit Contact' : 'Add Contact'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Contact name"
                autoFocus={!isEdit}
                data-testid="contact-name"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Phone Number *</Label>
              <Input
                value={formData.phone_number}
                onChange={e => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="e.g. 0212345678"
                readOnly={isEdit}
                className={isEdit ? 'bg-secondary/50 font-mono' : ''}
                data-testid="contact-phone"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={e => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company name"
                data-testid="contact-company"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Email</Label>
              <Input
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                type="email"
                data-testid="contact-email"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                data-testid="contact-notes"
              />
            </div>
            
            {/* Contact Type — always show the big buttons */}
            <div className="col-span-2">
              <Label>Contact Type</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, source: 'company' })}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.source === 'company'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-border hover:border-blue-500/50'
                  }`}
                  data-testid="contact-type-company"
                >
                  <Building2 className={`w-5 h-5 mb-2 ${formData.source === 'company' ? 'text-blue-400' : 'text-muted-foreground'}`} />
                  <div className={`font-medium ${formData.source === 'company' ? 'text-blue-400' : ''}`}>Company Contact</div>
                  <div className="text-xs text-muted-foreground">Shared with your entire team</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, source: 'personal' })}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.source === 'personal'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-border hover:border-purple-500/50'
                  }`}
                  data-testid="contact-type-personal"
                >
                  <User className={`w-5 h-5 mb-2 ${formData.source === 'personal' ? 'text-purple-400' : 'text-muted-foreground'}`} />
                  <div className={`font-medium ${formData.source === 'personal' ? 'text-purple-400' : ''}`}>Personal Contact</div>
                  <div className="text-xs text-muted-foreground">Only visible to you</div>
                </button>
              </div>
            </div>
            
            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  data-testid="contact-tag-input"
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !formData.name.trim()} data-testid="save-contact">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
